import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import axios, { API, formatError } from "../../lib/api";
import { useLang } from "../../lib/i18n";

const MONTHS_EN = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const EMPTY = { name: "", phone: "", email: "", village: "", material: "rice_husk", quantity_t: "", available_month: MONTHS_EN[new Date().getMonth()], notes: "" };

const Field = ({ id, label, children }) => (
  <div className="space-y-1.5"><Label htmlFor={id} className="text-[var(--sage)] text-xs font-mono uppercase tracking-wider">{label}</Label>{children}</div>
);

export const SupplyDialog = ({ open, onOpenChange }) => {
  const { t, lang } = useLang();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const materials = t("supply.materials");
  const months = t("supply.months");

  useEffect(() => {
    if (open) setDone(null);
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await axios.post(`${API}/supply`, { ...form, quantity_t: Number(form.quantity_t) || 0, lang });
      setDone({ ...res.data, phone: form.phone, email: form.email });
      setForm(EMPTY);
      toast.success(t("supply.toast", { ref: res.data.ref }));
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-lenis-prevent data-testid="supply-modal" className="max-w-xl max-h-[92vh] overflow-y-auto rounded-[28px] border hairline bg-[var(--forest-2)] text-[var(--cream)]">
        {done ? (
          <div data-testid="supply-success" className="py-10 text-center">
            <CheckCircle2 className="mx-auto h-14 w-14 text-[var(--mint)]" />
            <h3 className="mt-6 font-display text-3xl font-bold">{t("supply.doneTitle")}</h3>
            <p className="mt-2 text-[var(--sage)]">{t("supply.refPre")}<span data-testid="supply-ref" className="font-mono text-[var(--husk)]">{done.ref}</span>{t("supply.refPost")}</p>
            <p data-testid="supply-callback-note" className="mt-3 text-[var(--cream)]">{t("order.callOn", { phone: done.phone })}</p>
            {done.email && <p data-testid="supply-email-note" className="mt-1 text-sm text-[var(--sage)]">{t("order.mailTo", { email: done.email })}</p>}
            <button data-testid="supply-done-btn" onClick={() => onOpenChange(false)} className="mt-8 rounded-full bg-[var(--cream)] text-[var(--forest)] px-6 py-3 font-semibold">{t("supply.done")}</button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-3xl font-bold tracking-tight">{t("supply.title")}</DialogTitle>
              <DialogDescription className="text-[var(--sage)]">{t("supply.desc")}</DialogDescription>
            </DialogHeader>
            <form data-testid="supply-form" onSubmit={submit} className="grid gap-4 mt-2">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field id="s-material" label={t("supply.material")}>
                  <Select value={form.material} onValueChange={(v) => setForm({ ...form, material: v })}>
                    <SelectTrigger id="s-material" data-testid="supply-material-select" className="h-12"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(materials).map(([k, label]) => <SelectItem key={k} value={k} data-testid={`supply-material-${k}`}>{label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field id="s-qty" label={t("supply.qty")}><Input id="s-qty" data-testid="supply-input-quantity" type="number" min="0.5" step="0.5" required value={form.quantity_t} onChange={set("quantity_t")} className="h-12" /></Field>
                <Field id="s-month" label={t("supply.month")}>
                  <Select value={form.available_month} onValueChange={(v) => setForm({ ...form, available_month: v })}>
                    <SelectTrigger id="s-month" data-testid="supply-month-select" className="h-12"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {MONTHS_EN.map((m, i) => <SelectItem key={m} value={m}>{months[i]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field id="s-name" label={t("supply.name")}><Input id="s-name" data-testid="supply-input-name" required minLength={2} value={form.name} onChange={set("name")} className="h-12" /></Field>
                <Field id="s-phone" label={t("order.phoneHint")}><Input id="s-phone" data-testid="supply-input-phone" type="tel" required pattern="[0-9+\- ]{10,15}" value={form.phone} onChange={set("phone")} className="h-12" /></Field>
                <Field id="s-village" label={t("supply.village")}><Input id="s-village" data-testid="supply-input-village" required minLength={2} value={form.village} onChange={set("village")} className="h-12" /></Field>
              </div>
              <Field id="s-email" label={t("order.emailHint")}><Input id="s-email" data-testid="supply-input-email" type="email" maxLength={120} value={form.email} onChange={set("email")} className="h-12" /></Field>
              <Field id="s-notes" label={t("supply.notes")}><Input id="s-notes" data-testid="supply-input-notes" placeholder={t("supply.notesPh")} value={form.notes} onChange={set("notes")} className="h-12" /></Field>
              <button data-testid="supply-submit-button" disabled={busy} className="mt-2 h-14 rounded-full bg-[var(--husk)] text-[var(--forest)] font-semibold inline-flex items-center justify-center gap-2 transition-transform hover:-translate-y-0.5 disabled:opacity-60">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} {t("supply.submit")}
              </button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
